import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'

import {DopamineHistoryMessage, DopamineSessionSummary} from 'interfaces'
import {
  DopamineAssistantService,
  getDopamineAssistantBaseUrl,
  isDopamineAssistantNotFoundError,
} from 'services/api'
import {DopamineAssistantSessionController} from './DopamineAssistant.types'
import {
  createDopamineAssistantSessionId,
  DopamineAssistantStorageScope,
} from './DopamineAssistant.utils'

export const DOPAMINE_SESSIONS_QUERY_KEY = 'getDopamineAssistantSessions'
export const DOPAMINE_HISTORY_QUERY_KEY = 'getDopamineAssistantHistory'
const DOPAMINE_SESSION_WRITE_WINDOW_MS = 12 * 60 * 60 * 1000

const isSessionOutsideWriteWindow = (createdAt: string): boolean =>
  Date.now() - Date.parse(createdAt) >= DOPAMINE_SESSION_WRITE_WINDOW_MS

interface UseDopamineAssistantSessionOptions {
  enabled: boolean
  storageScope: DopamineAssistantStorageScope | null
}

type LocalSession = {
  summary: DopamineSessionSummary
  history: DopamineHistoryMessage[]
  isRunning: boolean
  completedMessageIds: string[]
}

// A history response can lag behind the stream. Do not replace the baseline
// with a partial saved turn (for example, a question whose answer is not saved yet).
const getReconciledHistory = (
  local: LocalSession,
  history: DopamineHistoryMessage[] | undefined,
): DopamineHistoryMessage[] => {
  // Keep queued submissions on the same baseline until the current run settles.
  if (local.isRunning) return local.history

  const savedIds = new Set(history?.map(message => message.id))
  return history && local.completedMessageIds.every(id => savedIds.has(id))
    ? history
    : local.history
}

export const useDopamineAssistantSession = ({
  enabled,
  storageScope,
}: UseDopamineAssistantSessionOptions): DopamineAssistantSessionController => {
  const isApiAvailable = Boolean(getDopamineAssistantBaseUrl())
  const isSessionApiEnabled = enabled && isApiAvailable && Boolean(storageScope)
  const queryClient = useQueryClient()
  const sessionsQueryKey = useMemo(
    () => [DOPAMINE_SESSIONS_QUERY_KEY, storageScope] as const,
    [storageScope],
  )
  const [clientSessionId, setClientSessionId] = useState(
    createDopamineAssistantSessionId,
  )
  const [conversationRevision, setConversationRevision] = useState(0)
  const [shouldLoadHistory, setShouldLoadHistory] = useState(false)
  const [hasConversation, setHasConversation] = useState(false)
  const [endedSessionIds, setEndedSessionIds] = useState<Set<string>>(new Set())
  const [localSessions, setLocalSessions] = useState<
    Record<string, LocalSession>
  >({})
  const shouldRefetchSessionsAfterFetchRef = useRef(false)
  const sessionsQuery = useQuery({
    enabled: isSessionApiEnabled,
    queryFn: DopamineAssistantService.getSessions,
    queryKey: sessionsQueryKey,
  })
  const historyQuery = useQuery({
    enabled: isSessionApiEnabled && shouldLoadHistory,
    queryFn: () => DopamineAssistantService.getSessionHistory(clientSessionId),
    queryKey: [DOPAMINE_HISTORY_QUERY_KEY, storageScope, clientSessionId],
  })
  const refetchSessions = sessionsQuery.refetch
  const refetchHistory = historyQuery.refetch

  useEffect(() => {
    if (
      !shouldRefetchSessionsAfterFetchRef.current ||
      sessionsQuery.fetchStatus !== 'idle'
    ) {
      return
    }

    shouldRefetchSessionsAfterFetchRef.current = false
    void refetchSessions()
  }, [refetchSessions, sessionsQuery.fetchStatus])

  const requestSessionsRefresh = useCallback((): void => {
    if (!isSessionApiEnabled) return

    const fetchStatus = queryClient.getQueryState(sessionsQueryKey)?.fetchStatus
    if (fetchStatus === 'idle' || fetchStatus === undefined) {
      void refetchSessions()
    } else {
      shouldRefetchSessionsAfterFetchRef.current = true
    }
  }, [isSessionApiEnabled, queryClient, refetchSessions, sessionsQueryKey])

  const startNewChat = useCallback((): void => {
    const nextSessionId = createDopamineAssistantSessionId()
    setClientSessionId(nextSessionId)
    setConversationRevision(revision => revision + 1)
    setShouldLoadHistory(false)
    setHasConversation(false)
  }, [])

  const selectSession = useCallback(
    (session: DopamineSessionSummary): void => {
      setClientSessionId(session.sessionId)
      setConversationRevision(revision => revision + 1)
      // Keep active streams local. Completed chats can refresh saved history.
      setShouldLoadHistory(!localSessions[session.sessionId]?.isRunning)
      setHasConversation(true)
      if (isSessionOutsideWriteWindow(session.createdAt)) {
        setEndedSessionIds(current => new Set(current).add(session.sessionId))
      }
    },
    [localSessions],
  )

  const markSessionSubmitted = useCallback(
    (title: string): void => {
      setHasConversation(true)
      setLocalSessions(current => {
        const existing = current[clientSessionId]
        if (existing)
          return {
            ...current,
            [clientSessionId]: {
              ...existing,
              isRunning: true,
              history: shouldLoadHistory
                ? getReconciledHistory(existing, historyQuery.data?.messages)
                : existing.history,
            },
          }
        return {
          ...current,
          [clientSessionId]: {
            isRunning: true,
            completedMessageIds: [],
            summary: sessionsQuery.data?.find(
              item => item.sessionId === clientSessionId,
            ) ?? {
              createdAt: new Date().toISOString(),
              sessionId: clientSessionId,
              title,
            },
            history: shouldLoadHistory ? historyQuery.data?.messages ?? [] : [],
          },
        }
      })
    },
    [clientSessionId, historyQuery.data, sessionsQuery.data, shouldLoadHistory],
  )

  const markSessionStarted = useCallback(
    (_sessionId: string): void => {
      requestSessionsRefresh()
    },
    [requestSessionsRefresh],
  )

  const markSessionSettled = useCallback(
    (sessionId: string, isRunning: boolean, messageId: string | null): void => {
      setLocalSessions(current => {
        const local = current[sessionId]
        if (!local) return current
        return {
          ...current,
          [sessionId]: {
            ...local,
            isRunning,
            completedMessageIds: messageId
              ? [...local.completedMessageIds, messageId]
              : local.completedMessageIds,
          },
        }
      })
      requestSessionsRefresh()
    },
    [requestSessionsRefresh],
  )

  const refreshSessions = requestSessionsRefresh

  const retryHistory = useCallback((): void => {
    void refetchHistory()
  }, [refetchHistory])

  const markSessionReadOnly = useCallback((sessionId: string): void => {
    setEndedSessionIds(current => new Set(current).add(sessionId))
  }, [])

  const remoteSessions = sessionsQuery.data ?? []
  const sessions = [
    ...Object.values(localSessions)
      .filter(
        ({summary}) =>
          !remoteSessions.some(item => item.sessionId === summary.sessionId),
      )
      .map(({summary}) => summary),
    ...remoteSessions,
  ].sort(
    (left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt),
  )
  const isHistoryLoading =
    historyQuery.isLoading && historyQuery.fetchStatus !== 'idle'
  const isSessionMissing =
    historyQuery.isError && isDopamineAssistantNotFoundError(historyQuery.error)
  const isHistoryError =
    historyQuery.isError &&
    (historyQuery.data === undefined || isSessionMissing)

  const localSession = localSessions[clientSessionId]
  const history = shouldLoadHistory ? historyQuery.data?.messages : undefined

  return {
    clientSessionId,
    conversationRevision,
    hasConversation,
    history: localSession
      ? getReconciledHistory(localSession, history)
      : history ?? [],
    isApiAvailable,
    // Newly created chats can briefly be absent from the history API even
    // after streaming finishes. Keep their local answer available during that gap.
    isHistoryError:
      shouldLoadHistory &&
      isHistoryError &&
      (!localSession || historyQuery.data !== undefined),
    isHistoryLoading: shouldLoadHistory && isHistoryLoading && !localSession,
    isReadOnly: endedSessionIds.has(clientSessionId),
    isHistoryRefreshing:
      shouldLoadHistory &&
      historyQuery.isFetching &&
      (!isHistoryLoading || Boolean(localSession)),
    isSessionsError: sessionsQuery.isError && sessionsQuery.data === undefined,
    isSessionsLoading:
      sessions.length === 0 &&
      sessionsQuery.isLoading &&
      sessionsQuery.fetchStatus !== 'idle',
    markSessionReadOnly,
    markSessionStarted,
    markSessionSubmitted,
    markSessionSettled,
    refreshSessions,
    retryHistory,
    selectSession,
    sessions,
    startNewChat,
  }
}
